<template>
  <div class="notice-form-page">
    <div class="notice-form-main">
      <el-card class="box-card" :class="{ 'header-stuck': headerStuck }">
      <div slot="header" class="card-header">
        <span class="card-title">{{ pageTitle }}</span>
        <div class="header-actions">
          <el-button size="small" @click="handleBack">返回列表</el-button>
          <el-button v-if="!isReadonly" type="primary" size="small" :loading="saving" @click="handleSubmit" v-permission="'btn:notice:save'">保存草稿</el-button>
        </div>
      </div>

      <el-form ref="form" :model="form" :rules="rules" :validate-on-rule-change="false" label-width="180px" size="small" class="notice-form" :disabled="isReadonly">
        <!-- 基础信息 -->
        <div class="section-title">通知类型<span class="config-desc">决定店主端交互形态</span></div>
        <el-form-item label="通知类型" prop="noticeType">
          <el-radio-group v-model="form.noticeType" :disabled="isEdit">
            <el-radio :label="1">活动通知</el-radio>
            <el-radio :label="2">系统通知</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 通知内容 -->
        <div class="section-title">通知内容</div>
        <el-form-item label="通知名称" prop="noticeName">
          <el-input v-model="form.noticeName" placeholder="请输入通知名称" maxlength="50" show-word-limit style="width: 500px"></el-input>
        </el-form-item>
        <el-form-item label="通知正文" prop="content" class="editor-form-item">
          <div class="editor-box" :class="{ 'is-readonly': isReadonly }">
            <quill-editor
              ref="quillEditor"
              v-model="form.content"
              :options="editorOption"
              :disabled="isReadonly"
            ></quill-editor>
          </div>
        </el-form-item>
        <el-form-item label="附件">
          <div class="attachment-box">
            <el-upload
              action="#"
              disabled
              :limit="9"
              :file-list="[]"
              class="attachment-uploader"
            >
              <el-button size="small" type="primary" disabled>上传附件</el-button>
              <div slot="tip" class="el-upload__tip">支持图片 / PDF，单文件 ≤ 10MB（功能暂未开发）</div>
            </el-upload>
          </div>
        </el-form-item>

        <!-- 阅读与确认配置 -->
        <div class="section-title">阅读与确认配置</div>
        <!-- 倒计时确认时间：活动通知与系统通知均可配置 -->
        <el-form-item class="config-item">
          <el-checkbox v-model="form.countdownConfirmOpen" :true-label="1" :false-label="0">倒计时确认时间</el-checkbox>
          <span class="config-desc">仅主按钮显示倒计时</span>
          <el-button class="preview-btn" type="text" size="mini" @click="preview('countdown')">预览</el-button>
        </el-form-item>
        <template v-if="form.countdownConfirmOpen === 1">
          <el-form-item prop="countdownConfirmSeconds">
            <el-input-number v-model="form.countdownConfirmSeconds" :min="0" :max="9999" :precision="0" label="秒" style="width: 100px"></el-input-number>
            <span class="form-unit">秒</span>
          </el-form-item>
        </template>

        <!-- 以下配置仅活动通知展示 -->
        <template v-if="form.noticeType === 1">
          <!-- 确认文案配置 -->
          <el-form-item class="config-item">
            <el-checkbox v-model="form.confirmTextOpen" :true-label="1" :false-label="0" disabled>确认文案配置</el-checkbox>
            <span class="config-desc">（默认开启，仅主表单显示）</span>
            <el-button class="preview-btn" type="text" size="mini" @click="preview('confirm')">预览</el-button>
          </el-form-item>

          <!-- 取消勾选“确认文案配置”后，下方到“首页弹窗”之前的配置项全部隐藏 -->
          <template v-if="form.confirmTextOpen === 1">
            <el-form-item label="确认文案" prop="confirmText">
              <el-input v-model="form.confirmText" type="textarea" :rows="2" placeholder="例如：我已阅读并同意统一调整门店年卡、月卡优惠价格..." style="width: 500px"></el-input>
            </el-form-item>
            <el-form-item label="主按钮名称" prop="mainButtonName">
              <el-input v-model="form.mainButtonName" maxlength="8" placeholder="例如：参加" style="width: 500px"></el-input>
            </el-form-item>
            <el-form-item label="副按钮名称" prop="subButtonName">
              <el-input v-model="form.subButtonName" maxlength="8" placeholder="例如：不参加" style="width: 500px"></el-input>
            </el-form-item>

            <!-- 允许店主修改选择 -->
            <el-form-item class="config-item">
              <el-checkbox v-model="form.isAllowModify" :true-label="1" :false-label="0" disabled>允许店主修改选择，最多可修改</el-checkbox>
            </el-form-item>
            <template v-if="form.isAllowModify === 1">
              <el-form-item prop="maxModifyCount">
                <el-input-number v-model="form.maxModifyCount" :min="0" :max="99" :precision="0" label="次" style="width: 100px"></el-input-number>
                <span class="form-unit">次</span>
                <span class="form-tip">（报名截止前有效）</span>
              </el-form-item>
            </template>

            <!-- 开启否定按钮选项理由 -->
            <el-form-item class="config-item">
              <el-checkbox v-model="form.isOpenNegReason" :true-label="1" :false-label="0">开启否定按钮选项理由</el-checkbox>
              <el-button class="preview-btn" type="text" size="mini" @click="preview('neg')">预览</el-button>
            </el-form-item>
            <template v-if="form.isOpenNegReason === 1">
              <el-form-item label="否定标题" prop="negTitle">
                <el-input v-model="form.negTitle" placeholder="例如：不参加原因反馈" style="width: 500px"></el-input>
              </el-form-item>
              <el-form-item label="否定说明" prop="negDesc">
                <el-input v-model="form.negDesc" type="textarea" :rows="3" placeholder="例如：请选择不参加的原因，帮助总部了解门店诉求..." style="width: 500px"></el-input>
              </el-form-item>
              <el-form-item label="否定理选择" prop="reasonSelectType">
                <el-radio-group v-model="form.reasonSelectType">
                  <el-radio :label="1">单选</el-radio>
                  <el-radio :label="2">多选</el-radio>
                </el-radio-group>
                <span class="example-text">（示例：</span>
                <el-link class="example-link" type="primary" :underline="false" @click="fillExample('单')">单选</el-link>
                <span class="example-text">/</span>
                <el-link class="example-link" type="primary" :underline="false" @click="fillExample('多')">多选</el-link>
                <span class="example-text">）</span>
              </el-form-item>
              <el-form-item label="理由选项" prop="negReasons">
                <div class="reason-builder">
                  <transition-group name="reason-list" tag="div">
                    <div v-for="(item, index) in form.negReasons" :key="index" class="reason-row">
                      <span class="reason-marker" :class="form.reasonSelectType === 1 ? 'is-radio' : 'is-checkbox'"></span>
                      <el-input
                        :value="form.negReasons[index]"
                        class="reason-input"
                        placeholder="请输入理由内容"
                        maxlength="50"
                        @input="onReasonInput(index, $event)"
                      ></el-input>
                      <el-button
                        class="reason-del"
                        type="text"
                        icon="el-icon-delete"
                        title="删除"
                        @click="removeReason(index)"
                      ></el-button>
                    </div>
                  </transition-group>
                  <div class="reason-hint">可增可改，保存时自动过滤空项</div>
                </div>
                <div class="reason-add" @click="addReason">
                  <i class="el-icon-plus"></i>
                  <span>添加理由</span>
                </div>
              </el-form-item>
              <el-form-item label="否定按钮取消名称" prop="negCancelButtonName">
                <el-input v-model="form.negCancelButtonName" maxlength="8" placeholder="例如：再想想" style="width: 500px"></el-input>
              </el-form-item>
              <el-form-item label="否定确认按钮名称" prop="negConfirmButtonName">
                <el-input v-model="form.negConfirmButtonName" maxlength="8" placeholder="例如：确认不参加" style="width: 500px"></el-input>
              </el-form-item>
            </template>
          </template>
        </template>

        <!-- 首页弹窗 -->
        <div class="section-title">首页弹窗</div>
        <el-form-item class="config-item">
          <el-checkbox v-model="form.isOpenHomePop" :true-label="1" :false-label="0">开启首页弹窗</el-checkbox>
        </el-form-item>
        <template v-if="form.isOpenHomePop === 1">
          <el-form-item label="弹窗标题" prop="homePopTitle">
            <el-input v-model="form.homePopTitle" placeholder="请输入首页弹窗标题" style="width: 500px"></el-input>
          </el-form-item>
          <el-form-item label="弹窗内容" prop="homePopContent">
            <el-input v-model="form.homePopContent" type="textarea" :rows="3" placeholder="请输入首页弹窗内容" style="width: 500px"></el-input>
          </el-form-item>
          <el-form-item label="首页弹窗主按钮名称" prop="homePopMainButtonName">
            <el-input v-model="form.homePopMainButtonName" maxlength="8" placeholder="例如：查看详情" style="width: 500px"></el-input>
          </el-form-item>
          <el-form-item label="首页弹窗主按钮行为" prop="homePopMainAction">
            <el-radio-group v-model="form.homePopMainAction">
              <el-radio :label="1">跳转通知详情</el-radio>
              <el-radio :label="2">仅关闭弹窗</el-radio>
            </el-radio-group>
          </el-form-item>

          <!-- 是否开启副按钮 -->
          <el-form-item class="config-item">
            <el-checkbox v-model="form.homePopSubOpen" :true-label="1" :false-label="0">是否开启副按钮</el-checkbox>
          </el-form-item>
          <template v-if="form.homePopSubOpen === 1">
            <el-form-item label="首页弹窗副按钮名称" prop="homePopSubButtonName">
              <el-input v-model="form.homePopSubButtonName" maxlength="8" placeholder="例如：暂不查看" style="width: 500px"></el-input>
            </el-form-item>
            <el-form-item label="首页弹窗副按钮行为" prop="homePopSubAction">
              <el-radio-group v-model="form.homePopSubAction">
                <el-radio :label="1">跳转通知详情</el-radio>
                <el-radio :label="2">仅关闭弹窗</el-radio>
              </el-radio-group>
            </el-form-item>
          </template>
        </template>

        <!-- 建议收集 -->
        <div class="section-title" v-if="form.noticeType === 1">建议收集</div>
        <template v-if="form.noticeType === 1">
          <el-form-item class="config-item">
            <el-checkbox v-model="form.suggestOpen" :true-label="1" :false-label="0">开启建议收集入口</el-checkbox>
          </el-form-item>
          <template v-if="form.suggestOpen === 1">
            <el-form-item label="入口文案" prop="suggestText">
              <el-input v-model="form.suggestText" placeholder="例如：您有什么建议？" style="width: 500px"></el-input>
            </el-form-item>
          </template>
        </template>

        <!-- 目标门店 -->
        <div class="section-title">目标门店</div>
        <el-form-item label="目标门店" prop="targetShopType">
          <el-radio-group v-model="form.targetShopType">
            <el-radio :label="1">全部门店</el-radio>
            <el-radio :label="2">指定门店</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="form.targetShopType === 2" label="选择门店" prop="shopIds">
          <div class="shop-box">
            <div class="shop-count">已选择 {{ form.shopIds.length }} 家门店</div>
            <el-button type="primary" size="small" @click="openShopDialog">选择门店</el-button>
            <el-tag
              v-for="(shop, index) in selectedShopNames"
              :key="index"
              :closable="!isReadonly"
              size="small"
              @close="removeShop(shop.id)"
              style="margin: 5px 5px 0 0"
            >{{ shop.name }}</el-tag>
          </div>
        </el-form-item>

        <!-- 发布方式 / 发布时间 / 报名截止时间已移到列表“发布”弹窗，发布时才填写 -->
      </el-form>
      </el-card>
    </div>

    <!-- 右侧手机预览：正文、按钮、各类弹窗实时跟随左侧配置 -->
    <div class="notice-form-aside">
      <notice-phone-preview ref="phonePreview" :form="form" />
    </div>

    <shop-select-dialog v-model="shopDialogVisible" :selected-ids="form.shopIds" @confirm="onShopConfirm"></shop-select-dialog>
  </div>
</template>

<script>
import ShopSelectDialog from '../common/ShopSelectDialog.vue'
import NoticePhonePreview from './NoticePhonePreview.vue'

export default {
  name: 'NoticeForm',
  components: { ShopSelectDialog, NoticePhonePreview },
  data() {
    return {
      isEdit: false,
      isCopy: false,
      // 只读详情：复用编辑页（控件禁用、隐藏“保存草稿”），由列表“详情”入口带 readonly=1 打开
      isReadonly: false,
      noticeId: null,
      form: {
        noticeType: 1,
        noticeName: '',
        content: '',
        isOpenHomePop: 1,
        homePopTitle: '',
        homePopContent: '',
        homePopMainButtonName: '',
        homePopMainAction: null,
        homePopSubOpen: 1,
        homePopSubButtonName: '',
        homePopSubAction: null,
        countdownConfirmOpen: 1,
        countdownConfirmSeconds: 8,
        confirmTextOpen: 1,
        confirmText: '我已阅读并同意统一调整门店年卡、月卡优惠价格，接受总部按活动规则统一配置直播商品，并承诺配合完成直播期间的商品上下架操作。',
        mainButtonName: '参加',
        subButtonName: '不参加',
        isAllowModify: 1,
        maxModifyCount: 1,
        isOpenNegReason: 1,
        negTitle: '不参加原因反馈',
        negDesc: '请选择不参加的原因，帮助总部了解门店诉求，后续可针对性优化活动方案。',
        reasonSelectType: 2,
        negCancelButtonName: '再想想',
        negConfirmButtonName: '确认不参加',
        negReasons: [
          '价格影响：统一调低价格影响门店利润空间',
          '档期冲突：活动期间门店已有其他安排',
          '门店原因：装修、检修或停业，无法参与'
        ],
        suggestOpen: 1,
        suggestText: '您有什么建议？',
        targetShopType: 1,
        shopIds: []
      },
      isFormDirty: false,
      saving: false,
      // 卡头是否处于吸顶状态：吸顶时补一层遮罩，避免头部上方漏出滚过的正文
      headerStuck: false,
      selectedShopNames: [],
      shopDialogVisible: false,
      editorOption: {
        placeholder: '请输入正文内容，支持加粗、标题、列表等富文本排版',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['clean']
          ]
        }
      },
      rules: {}
    }
  },
  computed: {
    pageTitle() {
      if (this.isReadonly) return '通知详情'
      if (this.isCopy) return '复制通知'
      return this.isEdit ? '编辑通知' : '新增通知'
    }
  },
  watch: {
    form: {
      handler() {
        this.isFormDirty = true
      },
      deep: true
    },
    'form.isOpenHomePop'() {
      this.resetHomePopFields()
      this.updateRules()
    },
    'form.homePopSubOpen'() {
      this.resetHomePopSubFields()
      this.updateRules()
    },
    'form.countdownConfirmOpen'() {
      this.resetCountdownFields()
      this.updateRules()
    },
    'form.confirmTextOpen'() {
      this.resetConfirmTextFields()
      this.updateRules()
    },
    'form.isAllowModify'() {
      this.resetModifyFields()
      this.updateRules()
    },
    'form.isOpenNegReason'() {
      this.resetNegReasonFields()
      this.updateRules()
    },
    'form.suggestOpen'() {
      this.resetSuggestFields()
      this.updateRules()
    },
    'form.noticeType'() {
      this.updateRules()
    },
    // 富文本输入不触发 el-form 的 change/blur，手动在内容变化时重校验正文（清空后立即提示必填）
    'form.content'() {
      if (this.$refs.form && this.rules.content) {
        this.$refs.form.validateField('content')
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    // 只读详情没有可保存内容，直接放行
    if (this.isReadonly) {
      next()
      return
    }
    if (this.isFormDirty) {
      this.$confirm('您有未保存的内容，确认离开当前页面？', '提示', {
        confirmButtonText: '离开',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        next()
      }).catch(() => {
        next(false)
      })
    } else {
      next()
    }
  },
  beforeDestroy() {
    this.unbindStickyHeader()
  },
  mounted() {
    this.rules = this.buildRules()
    // 卡头吸顶时补一层遮罩，盖住从头部上方滚过的正文，避免“漏光”
    this.bindStickyHeader()
    const id = this.$route.query.id
    const copyFrom = this.$route.query.copyFrom
    const readonly = this.$route.query.readonly
    // 只读详情必须带 id，否则按新增处理，避免落到“只读的新增页”
    this.isReadonly = (readonly === '1' || readonly === 'true') && !!id
    if (copyFrom && !this.isReadonly) {
      // 复制来源通知：二次编辑后另存为新草稿
      this.isCopy = true
      this.noticeId = Number(copyFrom)
      this.loadCopySource()
    } else if (id) {
      this.isEdit = true
      this.noticeId = Number(id)
      this.loadDetail()
    }
  },
  methods: {
    // 卡头吸顶检测：滚动时给卡片加 header-stuck，用伪元素遮住头部上方滚过的内容
    bindStickyHeader() {
      this.handleStickyScroll = () => {
        const header = this.$el && this.$el.querySelector('.el-card__header')
        if (!header) return
        const main = header.closest('.el-main')
        // 内容区滚动时以内容区顶部为准；若是整页滚动，则退化为视口顶部
        const limit = main ? Math.max(main.getBoundingClientRect().top, 0) : 0
        const stuck = header.getBoundingClientRect().top <= limit + 24
        if (stuck !== this.headerStuck) this.headerStuck = stuck
      }
      window.addEventListener('scroll', this.handleStickyScroll, true)
      window.addEventListener('resize', this.handleStickyScroll)
      this.handleStickyScroll()
    },
    unbindStickyHeader() {
      if (!this.handleStickyScroll) return
      window.removeEventListener('scroll', this.handleStickyScroll, true)
      window.removeEventListener('resize', this.handleStickyScroll)
      this.handleStickyScroll = null
    },
    buildRules() {
      // 按钮名称统一规则：2-8 个汉字或英文字母
      const buttonNameValidator = (rule, value, callback) => {
        if (!value) {
          callback()
          return
        }
        if (!/[\u4e00-\u9fa5a-zA-Z]{2,8}/.test(value)) {
          callback(new Error('请输入 2-8 个汉字或英文字母'))
        } else {
          callback()
        }
      }
      const buttonNameRule = (requiredMsg) => [
        { required: true, message: requiredMsg, trigger: 'blur' },
        { validator: buttonNameValidator, trigger: 'blur' }
      ]
      const baseRules = {
        noticeType: [{ required: true, message: '请选择通知类型', trigger: 'change' }],
        noticeName: [{ required: true, message: '请输入通知名称', trigger: 'blur' }],
        // 富文本空内容会是 '<p><br></p>' 这类非空 HTML，required 无法拦截，需剥离标签后校验真实文本
        content: [{
          validator: (rule, value, callback) => {
            const text = (value || '')
              .replace(/<[^>]+>/g, '')
              .replace(/&nbsp;/gi, ' ')
              .replace(/\s/g, '')
            if (!text) {
              callback(new Error('请输入正文内容'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }],
        targetShopType: [{ required: true, message: '请选择目标门店范围', trigger: 'change' }],
        shopIds: [{ required: true, message: '请选择目标门店', trigger: 'change' }]
      }
      if (this.form.targetShopType === 1) {
        delete baseRules.shopIds
      }
      if (this.form.isOpenHomePop === 1) {
        baseRules.homePopTitle = [{ required: true, message: '请输入弹窗标题', trigger: 'blur' }]
        baseRules.homePopContent = [{ required: true, message: '请输入弹窗内容', trigger: 'blur' }]
        baseRules.homePopMainButtonName = buttonNameRule('请输入首页弹窗主按钮名称')
        baseRules.homePopMainAction = [{ required: true, message: '请选择首页弹窗主按钮行为', trigger: 'change' }]
        if (this.form.homePopSubOpen === 1) {
          baseRules.homePopSubButtonName = buttonNameRule('请输入首页弹窗副按钮名称')
          baseRules.homePopSubAction = [{ required: true, message: '请选择首页弹窗副按钮行为', trigger: 'change' }]
        }
      }
      if (this.form.countdownConfirmOpen === 1) {
        baseRules.countdownConfirmSeconds = [{ required: true, message: '请输入倒计时确认时间', trigger: 'blur' }]
      }
      if (this.form.noticeType === 1 && this.form.confirmTextOpen === 1) {
        baseRules.confirmText = [{ required: true, message: '请输入确认文案', trigger: 'blur' }]
        baseRules.mainButtonName = buttonNameRule('请输入主按钮名称')
        baseRules.subButtonName = buttonNameRule('请输入副按钮名称')
      }
      if (this.form.noticeType === 1 && this.form.confirmTextOpen === 1 && this.form.isAllowModify === 1) {
        baseRules.maxModifyCount = [{ required: true, message: '请输入最多可修改次数', trigger: 'blur' }]
      }
      if (this.form.noticeType === 1 && this.form.confirmTextOpen === 1 && this.form.isOpenNegReason === 1) {
        baseRules.negTitle = [{ required: true, message: '请输入否定标题', trigger: 'blur' }]
        baseRules.negDesc = [{ required: true, message: '请输入否定说明', trigger: 'blur' }]
        baseRules.reasonSelectType = [{ required: true, message: '请选择否定理选择', trigger: 'change' }]
        baseRules.negCancelButtonName = buttonNameRule('请输入否定按钮取消名称')
        baseRules.negConfirmButtonName = buttonNameRule('请输入否定确认按钮名称')
        baseRules.negReasons = [{
          validator: (rule, value, callback) => {
            if (!value || value.filter(t => t && t.trim()).length === 0) {
              callback(new Error('请至少填写一个理由选项'))
            } else {
              callback()
            }
          },
          trigger: 'change'
        }]
      }
      if (this.form.noticeType === 1 && this.form.suggestOpen === 1) {
        baseRules.suggestText = [{ required: true, message: '请输入入口文案', trigger: 'blur' }]
      }
      return baseRules
    },
    updateRules() {
      this.rules = this.buildRules()
    },
    resetHomePopFields() {
      if (this.form.isOpenHomePop !== 1) {
        this.form.homePopTitle = ''
        this.form.homePopContent = ''
        this.form.homePopMainButtonName = ''
        this.form.homePopMainAction = null
        this.form.homePopSubOpen = 0
        this.form.homePopSubButtonName = ''
        this.form.homePopSubAction = null
      } else {
        if (!this.form.homePopMainAction) this.form.homePopMainAction = 1
        if (!this.form.homePopMainButtonName) this.form.homePopMainButtonName = '查看详情'
        this.resetHomePopSubFields()
      }
    },
    resetHomePopSubFields() {
      if (this.form.homePopSubOpen !== 1) {
        this.form.homePopSubButtonName = ''
        this.form.homePopSubAction = null
      } else {
        if (!this.form.homePopSubAction) this.form.homePopSubAction = 2
        if (!this.form.homePopSubButtonName) this.form.homePopSubButtonName = '暂不查看'
      }
    },
    resetCountdownFields() {
      if (this.form.countdownConfirmOpen !== 1) {
        this.form.countdownConfirmSeconds = 0
      }
    },
    resetConfirmTextFields() {
      if (this.form.confirmTextOpen !== 1) {
        this.form.confirmText = ''
        this.form.mainButtonName = ''
        this.form.subButtonName = ''
        // 级联清理“确认文案配置”下的子开关及其内容，保证重新勾选时是干净状态
        this.form.isAllowModify = 0
        this.form.maxModifyCount = 0
        this.form.isOpenNegReason = 0
        this.form.negTitle = ''
        this.form.negDesc = ''
        this.form.reasonSelectType = null
        this.form.negCancelButtonName = ''
        this.form.negConfirmButtonName = ''
        this.form.negReasons = []
      } else {
        if (!this.form.mainButtonName) this.form.mainButtonName = '参加'
        if (!this.form.subButtonName) this.form.subButtonName = '不参加'
      }
    },
    resetModifyFields() {
      if (this.form.isAllowModify !== 1) {
        this.form.maxModifyCount = 0
      }
    },
    resetNegReasonFields() {
      if (this.form.isOpenNegReason !== 1) {
        this.form.negTitle = ''
        this.form.negDesc = ''
        this.form.reasonSelectType = null
        this.form.negCancelButtonName = ''
        this.form.negConfirmButtonName = ''
        this.form.negReasons = []
      } else {
        if (!this.form.reasonSelectType) this.form.reasonSelectType = 1
        if (!this.form.negCancelButtonName) this.form.negCancelButtonName = '再想想'
        if (!this.form.negConfirmButtonName) this.form.negConfirmButtonName = '确认不参加'
      }
    },
    resetSuggestFields() {
      if (this.form.suggestOpen !== 1) {
        this.form.suggestText = ''
      }
    },
    // 表单里的“预览”按钮：把右侧手机预览切到对应场景
    preview(type) {
      const sceneMap = {
        neg: 'reason',
        suggest: 'suggest',
        homePop: 'homePop'
      }
      const scene = sceneMap[type] || 'detail'
      const preview = this.$refs.phonePreview
      if (!preview) return
      preview.goScene(scene)
      const el = document.querySelector('.notice-form-aside')
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    },
    fillExample(type) {
      this.form.reasonSelectType = type === '单' ? 1 : 2
      this.form.negReasons = type === '单'
        ? ['价格影响：统一调低价格影响门店利润空间']
        : [
            '价格影响：统一调低价格影响门店利润空间',
            '档期冲突：活动期间门店已有其他安排',
            '门店原因：装修、检修或停业，无法参与'
          ]
    },
    // keepPublish=true 用于编辑/详情回显；复制场景传 false，通知名称追加“-副本”
    fillForm(notice, keepPublish) {
      this.form = {
        noticeType: notice.noticeType,
        noticeName: keepPublish ? notice.noticeName : ((notice.noticeName || '') + '-副本'),
        content: notice.content || '',
        isOpenHomePop: notice.isOpenHomePop || 0,
        homePopTitle: notice.homePopTitle || '',
        homePopContent: notice.homePopContent || '',
        homePopMainButtonName: notice.homePopMainButtonName || '',
        homePopMainAction: notice.homePopMainAction,
        homePopSubOpen: notice.homePopSubOpen != null ? notice.homePopSubOpen : 1,
        homePopSubButtonName: notice.homePopSubButtonName || '',
        homePopSubAction: notice.homePopSubAction,
        countdownConfirmOpen: notice.countdownConfirmOpen != null ? notice.countdownConfirmOpen : 0,
        countdownConfirmSeconds: notice.countdownConfirmSeconds != null ? notice.countdownConfirmSeconds : 0,
        // 确认文案配置 / 允许店主修改选择：固定开启且界面置灰，回显（含历史数据）一律按开启处理
        confirmTextOpen: 1,
        confirmText: notice.confirmText || '',
        mainButtonName: notice.mainButtonName || '',
        subButtonName: notice.subButtonName || '',
        isAllowModify: 1,
        maxModifyCount: notice.maxModifyCount != null ? notice.maxModifyCount : 1,
        isOpenNegReason: notice.isOpenNegReason || 0,
        negTitle: notice.negTitle || '',
        negDesc: notice.negDesc || '',
        reasonSelectType: notice.reasonSelectType,
        negCancelButtonName: notice.negCancelButtonName || '',
        negConfirmButtonName: notice.negConfirmButtonName || '',
        negReasons: (notice.negReasonList || []).map(item => item.text),
        suggestOpen: notice.suggestOpen || 0,
        suggestText: notice.suggestText || '',
        targetShopType: notice.targetShopType || 1,
        shopIds: notice.shopIds || []
      }
    },
    async loadDetail() {
      try {
        const res = await this.$http.get('/shopNotice/detail/' + this.noticeId)
        if (res.data.status === 0) {
          const notice = res.data.data.notice
          this.fillForm(notice, true)
          this.selectedShopNames = (res.data.data.selectedShops || []).map(item => ({ id: item.id, name: item.name }))
          this.updateRules()
          // 回显赋值会触发 form 的 deep watch 把 dirty 置为 true，等 watch 队列 flush 后复位，避免“未保存”误报
          this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.clearValidate()
            this.isFormDirty = false
          })
        } else {
          this.$message.error(res.data.msg || '获取详情失败')
          this.$router.push('/noticeList')
        }
      } catch (error) {
        console.error('获取通知详情失败:', error)
        this.$message.error('获取通知详情失败')
        this.$router.push('/noticeList')
      }
    },
    // 复制来源通知：回填内容后清空 id，保存时走新增分支生成新草稿
    async loadCopySource() {
      try {
        const res = await this.$http.get('/shopNotice/detail/' + this.noticeId)
        if (res.data.status === 0) {
          const notice = res.data.data.notice
          this.fillForm(notice, false)
          this.noticeId = null
          this.selectedShopNames = (res.data.data.selectedShops || []).map(item => ({ id: item.id, name: item.name }))
          this.updateRules()
          // 回填后再清一次校验状态，确保进入页面不带任何残留红字
          this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.clearValidate()
            this.isFormDirty = false
          })
        } else {
          this.$message.error(res.data.msg || '获取通知详情失败')
          this.$router.push('/noticeList')
        }
      } catch (error) {
        console.error('获取通知详情失败:', error)
        this.$message.error('获取通知详情失败')
        this.$router.push('/noticeList')
      }
    },
    async loadShopNames(shopIds) {
      if (!shopIds || shopIds.length === 0) {
        this.selectedShopNames = []
        return
      }
      try {
        const res = await this.$http.get('/storelist/', { params: { pagenum: 1, pagesize: 9999 } })
        if (res.data.status === 1 && res.data.data && res.data.data.list) {
          const list = res.data.data.list.map(item => ({ id: item.pk, name: item.fields.storename }))
          this.selectedShopNames = list.filter(item => shopIds.includes(item.id))
        }
      } catch (error) {
        console.error('获取门店名称失败:', error)
      }
    },
    // 返回列表页：导航真正成功后再关闭编辑标签，避免用户在有未保存内容时选“取消”导致标签已被关掉
    handleBack() {
      this.$router.push('/noticeList', () => {
        this.closeEditTab()
      })
    },
    openShopDialog() {
      this.shopDialogVisible = true
    },
    async onShopConfirm(shopIds, shopRows) {
      this.form.shopIds = shopIds
      const rows = Array.isArray(shopRows) ? shopRows.filter(r => r && r.id) : []
      // 弹窗按页加载门店，编辑回显的已选门店若不在当前页，回传行里只有 id 没有 storename。
      // 先用页面上已有的名字兜底（详情回显/上一轮选择都留在 selectedShopNames 里），
      // 都没有才退化为「门店+ID」，避免打开弹窗确认一次后真名被覆盖成「门店88」
      const nameMap = {}
      this.selectedShopNames.forEach(s => { nameMap[s.id] = s.name })
      if (rows.length >= shopIds.length) {
        this.selectedShopNames = rows.map(r => ({ id: r.id, name: r.storename || nameMap[r.id] || ('门店' + r.id) }))
      } else {
        await this.loadShopNames(shopIds)
      }
    },
    removeShop(shopId) {
      this.form.shopIds = this.form.shopIds.filter(id => id !== shopId)
      this.selectedShopNames = this.selectedShopNames.filter(item => item.id !== shopId)
    },
    // Vue2 侦测不到数组下标赋值，用 $set 写回，保证右侧预览等依赖方实时更新
    onReasonInput(index, value) {
      this.$set(this.form.negReasons, index, value)
    },
    addReason() {
      this.form.negReasons.push('')
    },
    removeReason(index) {
      this.form.negReasons.splice(index, 1)
    },
    handleSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return
        if (this.form.targetShopType === 2 && this.form.shopIds.length === 0) {
          this.$message.warning('请选择目标门店')
          return
        }
        // 只保存草稿：发布方式/发布时间/报名截止时间由列表“发布”弹窗提交
        await this.saveNotice(this.buildPayload())
      })
    },
    buildPayload() {
      const isActivity = this.form.noticeType === 1
      return {
        id: this.isEdit ? this.noticeId : null,
        noticeType: this.form.noticeType,
        noticeName: this.form.noticeName,
        content: this.form.content,
        isOpenHomePop: this.form.isOpenHomePop,
        homePopTitle: this.form.isOpenHomePop === 1 ? this.form.homePopTitle : null,
        homePopContent: this.form.isOpenHomePop === 1 ? this.form.homePopContent : null,
        homePopMainButtonName: this.form.isOpenHomePop === 1 ? this.form.homePopMainButtonName : null,
        homePopMainAction: this.form.isOpenHomePop === 1 ? this.form.homePopMainAction : null,
        homePopSubOpen: this.form.isOpenHomePop === 1 ? this.form.homePopSubOpen : 0,
        homePopSubButtonName: (this.form.isOpenHomePop === 1 && this.form.homePopSubOpen === 1) ? this.form.homePopSubButtonName : null,
        homePopSubAction: (this.form.isOpenHomePop === 1 && this.form.homePopSubOpen === 1) ? this.form.homePopSubAction : null,
        countdownConfirmOpen: this.form.countdownConfirmOpen,
        countdownConfirmSeconds: this.form.countdownConfirmOpen === 1 ? this.form.countdownConfirmSeconds : null,
        confirmTextOpen: isActivity ? this.form.confirmTextOpen : 0,
        confirmText: (isActivity && this.form.confirmTextOpen === 1) ? this.form.confirmText : null,
        mainButtonName: (isActivity && this.form.confirmTextOpen === 1) ? this.form.mainButtonName : null,
        subButtonName: (isActivity && this.form.confirmTextOpen === 1) ? this.form.subButtonName : null,
        isAllowModify: (isActivity && this.form.confirmTextOpen === 1) ? this.form.isAllowModify : 0,
        maxModifyCount: (isActivity && this.form.confirmTextOpen === 1 && this.form.isAllowModify === 1) ? this.form.maxModifyCount : null,
        isOpenNegReason: (isActivity && this.form.confirmTextOpen === 1) ? this.form.isOpenNegReason : 0,
        negTitle: (isActivity && this.form.confirmTextOpen === 1 && this.form.isOpenNegReason === 1) ? this.form.negTitle : null,
        negDesc: (isActivity && this.form.confirmTextOpen === 1 && this.form.isOpenNegReason === 1) ? this.form.negDesc : null,
        reasonSelectType: (isActivity && this.form.confirmTextOpen === 1 && this.form.isOpenNegReason === 1) ? this.form.reasonSelectType : null,
        negCancelButtonName: (isActivity && this.form.confirmTextOpen === 1 && this.form.isOpenNegReason === 1) ? this.form.negCancelButtonName : null,
        negConfirmButtonName: (isActivity && this.form.confirmTextOpen === 1 && this.form.isOpenNegReason === 1) ? this.form.negConfirmButtonName : null,
        negReasons: (isActivity && this.form.confirmTextOpen === 1 && this.form.isOpenNegReason === 1) ? this.form.negReasons.filter(t => t && t.trim()) : [],
        suggestOpen: isActivity ? this.form.suggestOpen : 0,
        suggestText: (isActivity && this.form.suggestOpen === 1) ? this.form.suggestText : null,
        targetShopType: this.form.targetShopType,
        shopIds: this.form.targetShopType === 1 ? null : this.form.shopIds
      }
    },
    // 通知 TabsView 关闭当前编辑页标签
    closeEditTab() {
      this.$root.$emit('tabs:close', this.$route.path)
    },
    /**
     * 保存通知草稿：只存“发给哪些门店”，发布方式/发布时间/报名截止时间由列表“发布”弹窗提交
     */
    async saveNotice(payload) {
      if (this.saving) return
      this.saving = true
      try {
        const res = await this.$http.post('/shopNotice/saveDraft', payload)
        if (res.data.status === 0) {
          this.$message.success('草稿保存成功')
          this.isFormDirty = false
          this.closeEditTab()
          this.$router.push('/noticeList')
        } else {
          this.$message.error(res.data.msg || '保存失败')
        }
      } catch (error) {
        console.error('保存通知失败:', error)
        this.$message.error('保存通知失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.notice-form-page {
  padding: 20px;
  display: flex;
  align-items: flex-start;
  /* 整体固定版心：宽屏下不再无限拉伸，水平居中 */
  width: 1240px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}
/* 左侧表单区：固定占据版心剩余宽度（约 870px），右侧留给手机预览 */
.notice-form-main {
  flex: 1 1 auto;
  min-width: 0;
}
/* 右侧手机预览：跟随页面滚动吸顶 */
.notice-form-aside {
  flex: none;
  width: 350px;
  margin-left: 20px;
  position: sticky;
  top: 20px;
}
.notice-form {
  padding-bottom: 0;
}
/* 右侧预览占位后表单区变窄：固定宽度的输入框自动收缩，避免撑破卡片 */
.notice-form ::v-deep .el-input,
.notice-form ::v-deep .el-textarea,
.notice-form ::v-deep .el-input-number,
.notice-form ::v-deep .el-date-editor {
  max-width: 100%;
}
/* label-width 已放宽到 180px，这里禁止 label 折行，避免出现“两行标题” */
.notice-form ::v-deep .el-form-item__label {
  white-space: nowrap;
  color: #606266;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: bold;
  color: #303133;
  margin: 26px 0 16px 0;
  padding: 0 0 8px 10px;
  border-left: 4px solid #409eff;
  border-bottom: 1px solid #f0f2f5;
}
.section-title:first-child {
  margin-top: 0;
}
.section-title .config-desc {
  font-weight: normal;
  vertical-align: middle;
}
.config-item {
  margin-bottom: 5px;
}
/* 功能开关作为模块内的二级标题：整体左移，取消无 label 表单项继承的 130px 标签缩进 */
.config-item ::v-deep .el-form-item__content {
  margin-left: 60px !important;
}
.config-item ::v-deep .el-checkbox__label {
  font-size: 15px;
  font-weight: 600;
}
.config-desc {
  color: #909399;
  font-size: 12px;
  margin-left: 4px;
}
.preview-btn {
  float: right;
  margin-right: 40px;
  color: #e6a23c;
}
.example-text {
  margin-left: 16px;
  color: #909399;
  font-size: 12px;
}
.example-link {
  margin-left: 4px;
  font-size: 12px;
}
.form-tip {
  margin-left: 10px;
  color: #999;
  font-size: 12px;
}
.form-unit {
  margin-left: 8px;
  color: #606266;
}
.editor-box {
  width: 100%;
  /* 整体提升为独立层叠上下文，避免下方“附件”等 position:relative 的表单项盖住工具条下拉面板 */
  position: relative;
  z-index: 10;
}
/* 只读详情：富文本不可编辑，但保留正文滚动条（编辑禁用交给 Quill 的 :disabled，
   其内部 quill.enable(false) 只禁编辑不清滚动）。这里只做视觉区隔，不再禁用容器交互 */
.editor-box.is-readonly ::v-deep .ql-container {
  background: #fafafa;
}
.editor-box.is-readonly ::v-deep .ql-toolbar {
  pointer-events: none;
  opacity: 0.6;
}
/* 富文本编辑器：限制编辑器最大高度并内部滚动，避免内容撑高溢出后盖住下方提示文字。
   用 max-height 放在 .ql-editor 上（而不是固定 .ql-container + overflow:hidden 裁切），
   让滚动条直接在编辑器内容区生效，避免底部内容/滚动条被父容器或下方元素挡住 */
.editor-box ::v-deep .ql-container {
  height: auto;
  overflow: visible;
}
.editor-box ::v-deep .ql-editor {
  min-height: 120px;
  max-height: 240px;
  overflow-y: auto;
  line-height: 1.7;
}
.editor-box ::v-deep .ql-editor.ql-blank::before {
  font-style: normal;
  color: #c0c4cc;
}
.editor-box ::v-deep .ql-toolbar.ql-snow,
.editor-box ::v-deep .ql-container.ql-snow {
  border-color: #dcdfe6;
}

.editor-box ::v-deep .ql-toolbar {
  position: relative;
  z-index: 20;
}
.editor-box ::v-deep .ql-picker-options {
  position: absolute;
  top: auto !important;
  bottom: 100% !important;
  z-index: 30 !important;
  max-height: 220px !important;
  overflow-y: auto !important;
}

.editor-form-item {
  position: relative;
  z-index: 15;
}
.editor-tip {
  margin-top: 8px;
  color: #999;
  font-size: 12px;
  line-height: 1.5;
}
.attachment-box {
  padding: 15px;
  text-align: center;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background: #fafafa;
}
.reason-builder {
  width: 500px;
  max-width: 100%;
  box-sizing: border-box;
  background: #f7f8fa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 14px;
}
.reason-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.reason-row:last-of-type {
  margin-bottom: 0;
}
.reason-marker {
  flex: none;
  width: 16px;
  height: 16px;
  margin-right: 10px;
  border: 1px solid #c0c4cc;
  background: #fff;
  box-sizing: border-box;
}
.reason-marker.is-radio {
  border-radius: 50%;
}
.reason-marker.is-checkbox {
  border-radius: 3px;
}
.reason-input {
  flex: 1;
}
.reason-input ::v-deep .el-input__inner {
  height: 30px;
  line-height: 30px;
  padding: 0;
  border: none;
  border-bottom: 1px solid #dcdfe6;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
.reason-input ::v-deep .el-input__inner:hover {
  border-bottom-color: #b0b3b8;
}
.reason-input ::v-deep .el-input__inner:focus {
  border-bottom-color: #409eff;
}
.reason-del {
  flex: none;
  margin-left: 10px;
  padding: 4px 2px;
  color: #909399;
}
.reason-del:hover {
  color: #f56c6c;
}
.reason-add {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 500px;
  max-width: 100%;
  box-sizing: border-box;
  height: 32px;
  margin-top: 12px;
  border: 1px dashed #c0c4cc;
  border-radius: 4px;
  color: #909399;
  cursor: pointer;
  transition: all 0.2s;
}
.reason-add:hover {
  color: #409eff;
  border-color: #409eff;
}
.reason-add i {
  margin-right: 4px;
}
.reason-hint {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}
.reason-list-enter-active,
.reason-list-leave-active {
  transition: all 0.25s;
}
.reason-list-enter,
.reason-list-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.shop-box {
  padding: 15px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background: #fafafa;
}
.shop-count {
  margin-bottom: 10px;
  color: #606266;
}
/* el-card 自带 overflow: hidden，会让内部 sticky 失效，这里放开才能真正吸顶 */
.box-card {
  overflow: visible;
}
/* 卡片头部吸顶：标题与操作按钮滚动时始终可见，正文从头部下方滚过 */
.box-card ::v-deep .el-card__header {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  border-radius: 4px 4px 0 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
/* 吸顶时头部上方会露出滚过的正文，用一块同色遮罩盖住（仅在吸顶状态下显示） */
.box-card.header-stuck ::v-deep .el-card__header::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 48px;
  background: #fff;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}
.header-actions {
  display: flex;
  align-items: center;
}
/* 窄屏：预览面板收窄，把宽度让给表单，保证输入框固定 500px 不被压缩 */
@media screen and (max-width: 1280px) {
  .notice-form-aside {
    width: 290px;
    margin-left: 16px;
  }
}
/* 表单项统一为“标签 + 固定 500px 输入框”，窄屏下由 max-width 兜底收缩 */
</style>
